import type { LandingV3Content } from './landingV3Content'
import homepageFounderPortrait from '@/assets/homepage-founder-portrait.webp'
import briefingActionSketch from '@/assets/briefing-action-sketch.svg'
import governanceSketch from '@/assets/governance-sketch.svg'
import accountMonitoringSketch from '@/assets/account-monitoring-sketch.svg'
import { IntegrationLogoStrip } from '@/components/Public/IntegrationLogoStrip'

interface LandingV3ExperienceProps {
  content: LandingV3Content
}

// The canonical distinct-sources strip. CRM stays: this names the systems
// Ricald CONNECTS, and Ricald's own pipeline is not one of them. Putting
// 'Pipeline' in CRM's slot rewrites the positioning from "connects your
// distinct sources" to "replaces one of them" — the native record is an
// additive fallback for teams without a CRM, not a substitute for the claim.
const sources = ['CRM', 'Ads', 'Email', 'Calendar', 'Network', 'Website']

const workItems = [
  {
    kind: 'Renewal window', kindClass: 'renewal',
    acct: 'Meridian Health',
    why: 'Renewal opens in 60 days \u2014 your champion left in March. The new VP Ops hasn\u2019t heard from you.',
    move: 'Re-engage email to the new VP Ops, drafted',
    chip: 'Waiting for your approval', chipClass: 'wait',
  },
  {
    kind: 'Champion moved', kindClass: 'champion',
    acct: 'Trask Manufacturing',
    why: 'A power user at one of your customers just became VP Operations here.',
    move: 'Congratulations sent, intro queued to follow',
    chip: 'Ran inside your autonomy rules', chipClass: 'auto',
  },
  {
    kind: 'Going quiet', kindClass: 'quiet',
    acct: 'Ostara Labs',
    why: 'Usage down 40% over six weeks, renewal in Q4.',
    move: 'Check-in call proposed; nurture continues',
    chip: 'On your approved cadence', chipClass: 'auto',
  },
  {
    kind: 'Authority limit', kindClass: 'authority',
    acct: 'Northstar',
    why: 'The proposed concession exceeds your 5% limit.',
    move: 'Revised annual offer prepared',
    chip: 'Paused for your decision', chipClass: 'you',
  },
]

const events = [
  ['Demo completed', 'THIS MONTH · 142'],
  ['Referral received', 'UNACKNOWLEDGED · 38'],
  ['Pricing page revisited', 'NO FOLLOW-UP · 24.6K'],
  ['Partner interest', 'UNROUTED · 17'],
  ['Pipeline stalled', 'NO NEXT ACTION · 89'],
]

const systems = [
  ['CRM', 'Stale fields'],
  ['Ad platforms', 'Engagement isolated'],
  ['Email', 'Threads lack ownership'],
  ['Slack', 'Context in channels'],
  ['Calendar', 'Meetings lack outcomes'],
  ['Spreadsheets', 'Already outdated'],
]

const contextNodes = [
  ['Deal state', 'Ricald or CRM stage · opportunity history'],
  ['Engagement', 'Ad activity · email · website'],
  ['Relationships', 'Warm paths · buying committee'],
  ['Commitments', 'Meetings · unresolved actions'],
  ['Operating policy', 'Authority · thresholds · voice'],
  ['Shared GTM memory', 'Decisions · outcomes · lessons'],
]

const evidence = [
  'CRM · negotiation',
  'Email · 3 opens',
  'Calendar · CFO attended',
  'Network · warm route',
]

const metrics = [
  ['Investigated', '12', '4 qualified'],
  ['Advanced', '4', '+2 this week'],
  ['Completed', '9', 'within policy'],
  ['Needs you', '1', 'high impact'],
]

function NoteStack({ notes }: { notes: Array<{ title: string; body: string }> }) {
  return (
    <div className="lv3-note-stack">
      {notes.map((note) => (
        <article className="lv3-note" key={note.title}>
          <h3>{note.title}</h3>
          <p>{note.body}</p>
        </article>
      ))}
    </div>
  )
}

function NumberedSteps({ steps }: { steps: Array<{ title: string; body: string }> }) {
  return (
    <div className="lv3-action-list">
      {steps.map((step, index) => (
        <article className="lv3-action-row" key={step.title}>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <div>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
          </div>
        </article>
      ))}
    </div>
  )
}

function Hero({ content }: { content: LandingV3Content['hero'] }) {
  return (
    <header className="lv3-hero">
      <div className="lv3-wrap">
        <div className="lv3-hero-copy">
          <p className="lv3-pill"><i />{content.badge}</p>
          <h1>{content.title}</h1>
          <p className="lv3-lead">{content.body}</p>
          <div className="lv3-hero-actions">
            <a className="lv3-button lv3-button-primary" href="#preview">
              {content.primaryCta} <span aria-hidden="true">→</span>
            </a>
            <a className="lv3-button lv3-button-secondary" href="#mechanism">
              {content.secondaryCta}
            </a>
          </div>
          <p className="lv3-caption">{content.caption}</p>
        </div>

        <section className="lv3-operating-view" id="preview" aria-label="Ricald GTM operation preview">
          <div className="lv3-appbar">
            <div className="lv3-appname"><i /> RICALD / GTM OPERATIONS</div>
            <div className="lv3-appmeta"><span>14:08 BERLIN</span><span>ALL SYSTEMS ACTIVE</span></div>
          </div>
          <div className="lv3-surface">
            <aside className="lv3-rail" aria-label="Product preview navigation">
              {/* Understand → Coordinate → Decide. The groups now read as the
                  order the work actually happens in, and each verb says what the
                  user does there. "Operate / Understand / Govern" named postures
                  rather than actions, and put the abstract one first. */}
              <p>Understand</p>
              <span>Accounts</span><span>Outcomes</span><span>Memory</span>
              <p>Coordinate</p>
              <span className="active">Today <b>1</b></span>
              <span>Objectives <b>3</b></span>
              <span>Work in motion <b>12</b></span>
              <p>Decide</p>
              <span>Decisions <b>1</b></span>
              <span>Authority</span><span>Activity</span>
            </aside>
            <div className="lv3-workspace">
              <div className="lv3-workspace-head">
                {/* The statement leads. "What requires attention" was a label
                    competing with the only line that says how things are. */}
                <div><h2>Your operation is moving.<br />One decision needs you.</h2></div>
                <p>UPDATED 12 SECONDS AGO</p>
              </div>
              <IntegrationLogoStrip className="lv3-integration-logos" />

              <div className="lv3-context-flow">
                <div className="lv3-sources">
                  {sources.map((source) => <span key={source}><i />{source}</span>)}
                </div>
                <span className="lv3-flow-arrow" aria-hidden="true">→</span>
                <article className="lv3-next-action">
                  <p>Connected context → next action</p>
                  <h3>Advance Northstar with a revised annual offer</h3>
                  {/* States the conclusion, not the machinery. "Six systems
                      agree" made the reader do the inference, and the source row
                      immediately left already shows the count. */}
                  <span>Buying intent is rising — the security review cleared and a trusted route to the CFO exists.</span>
                </article>
              </div>

              <article className="lv3-objective">
                <div>
                  <p>GTM objective</p>
                  <h3>Grow qualified pipeline in European fintech</h3>
                  <span>Ricald is coordinating account research, qualification, and outreach against this outcome.</span>
                </div>
                <div className="lv3-progress"><b>68%</b><i><span /></i></div>
              </article>

              <div className="lv3-ops-grid">
                <section className="lv3-panel">
                  <div className="lv3-panel-head"><h3>Work in motion</h3><span>12 ACTIVE</span></div>
                  {workItems.map((item) => (
                    <article className="lv3-motion-row" key={item.acct}>
                      <div className="lv3-motion-head">
                        <h4>{item.acct}</h4>
                        <span className={`lv3-motion-kind ${item.kindClass}`}>{item.kind}</span>
                      </div>
                      <p>{item.why}</p>
                      <div className="lv3-motion-move">
                        <em>{item.move}</em>
                        <span className={`lv3-motion-chip ${item.chipClass}`}>{item.chip}</span>
                      </div>
                    </article>
                  ))}
                </section>
                <aside className="lv3-panel lv3-decision">
                  <div className="lv3-panel-head"><h3>Decision for you</h3><span>CONFIDENCE 84%</span></div>
                  <div className="lv3-decision-body">
                    <p className="lv3-urgent">Approval required</p>
                    <h3>Approve the revised enterprise offer for Northstar?</h3>
                    <p>Ricald recommends a 7% concession in exchange for an annual commitment.</p>
                    <div className="lv3-evidence">{evidence.map((item) => <span key={item}>{item}</span>)}</div>
                    <div className="lv3-reason-grid">
                      <div><span>Why now</span><b>Security review cleared</b></div>
                      <div><span>Expected effect</span><b>+18% close likelihood</b></div>
                      <div><span>Relationship</span><b>Trusted CFO route found</b></div>
                      <div><span>Your limit</span><b>5% without review</b></div>
                    </div>
                    <div className="lv3-mini-actions"><button>Inspect evidence</button><button>Modify</button><button>Approve &amp; send</button></div>
                  </div>
                </aside>
              </div>

              <div className="lv3-metrics">
                {metrics.map(([label, value, detail]) => (
                  <article key={label}><span>{label}</span><b>{value}</b><small>{detail}</small></article>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </header>
  )
}

function ContextGap({ content }: { content: LandingV3Content['context'] }) {
  return (
    <section className="lv3-context-gap" id="problem">
      <div className="lv3-wrap">
        <div className="lv3-section-head">
          <p className="lv3-kicker">{content.kicker}</p>
          <h2>{content.title}</h2>
          {content.lead && content.lead !== content.title && (
            <p className="lv3-context-lead">{content.lead}</p>
          )}
          <p>{content.body}</p>
        </div>

        <div className="lv3-gap-grid">
          <div className="lv3-dark-bank">
            <p className="lv3-bank-title">Live GTM events <i /></p>
            {events.map(([event, status]) => <div className="lv3-dark-row" key={event}><b>{event}</b><span>{status}</span></div>)}
          </div>
          <div className="lv3-context-loss"><span>Context lost<br />between tools</span></div>
          <div className="lv3-dark-bank">
            <p className="lv3-bank-title bad">Fragmented systems <i /></p>
            <div className="lv3-silo-grid">
              {systems.map(([system, status]) => <div key={system}><b>{system}</b><span>{status}</span></div>)}
            </div>
            <p className="lv3-bank-note">No shared memory. No agreed next action.</p>
          </div>
        </div>

        <div className="lv3-transformation">
          <div className="lv3-section-head">
            <p className="lv3-kicker">{content.actionKicker}</p>
            <h2>{content.actionTitle}</h2>
          </div>
          <div className="lv3-transform-grid">
            <div className="lv3-context-nodes">
              {contextNodes.map(([title, body]) => <div key={title}><b>{title}</b><span>{body}</span></div>)}
            </div>
            <div className="lv3-core" aria-label="Ricald connects the context"><span>R</span></div>
            <article className="lv3-action-output">
              <p>Next action · confidence 84%</p>
              <h3>Advance Northstar with a revised annual offer.</h3>
              <span>Demand is active, the security blocker has cleared, and a trusted CFO route exists. The proposed concession exceeds autonomous authority.</span>
              <div>{evidence.map((item) => <small key={item}>{item}</small>)}</div>
              <footer><button>Inspect evidence</button><button>Modify</button><button>Approve &amp; send</button></footer>
            </article>
          </div>
          <p className="lv3-flow-labels">DISCOVER <i>→</i> CONNECT <i>→</i> REASON <i>→</i> DECIDE <i>→</i> ACT <i>→</i> LEARN</p>
        </div>
      </div>
    </section>
  )
}

function StorySections({ content }: { content: LandingV3Content }) {
  return (
    <>
      <section className="lv3-story-section">
        <div className="lv3-wrap lv3-story-grid">
          <div className="lv3-story-copy">
            <p className="lv3-kicker">{content.morning.kicker}</p>
            <h2>{content.morning.title}</h2>
            <p>{content.morning.body}</p>
            <NoteStack notes={content.morning.notes} />
          </div>
          <figure className="lv3-image-frame lv3-human-frame">
            <img src={homepageFounderPortrait} alt="A founder reviewing the commercial decision that needs judgment." />
            <figcaption>
              <span>{content.morning.overlayLabel}</span>
              <b>{content.morning.overlayTitle}</b>
              <p>{content.morning.overlayBody}</p>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="lv3-story-section elevated" id="mechanism">
        <div className="lv3-wrap">
          <div className="lv3-story-heading">
            <p className="lv3-kicker">{content.action.kicker}</p>
            <h2>{content.action.title}</h2>
            <p>{content.action.body}</p>
          </div>
          <div className="lv3-story-grid reverse">
            <figure className="lv3-image-frame"><img src={briefingActionSketch} alt="Sketch: an account briefing, the warm introduction route it surfaces, and the approved next action." /></figure>
            <div className="lv3-story-copy">
              <p className="lv3-kicker">{content.action.detailKicker}</p>
              <h2>{content.action.detailTitle}</h2>
              <p>{content.action.detailBody}</p>
              <NumberedSteps steps={content.action.steps} />
            </div>
          </div>
        </div>
      </section>

      <section className="lv3-story-section" id="operation">
        <div className="lv3-wrap lv3-story-grid">
          <div className="lv3-story-copy">
            <p className="lv3-kicker">{content.authority.kicker}</p>
            <h2>{content.authority.title}</h2>
            <p>{content.authority.body}</p>
            <NoteStack notes={content.authority.notes} />
          </div>
          <figure className="lv3-image-frame"><img src={governanceSketch} alt="Ricald governance flow showing draft, approval, send, and audit trail." /></figure>
        </div>
      </section>

      <section className="lv3-story-section elevated">
        <div className="lv3-wrap lv3-story-grid reverse">
          <figure className="lv3-image-frame"><img src={accountMonitoringSketch} alt="Ricald monitoring context and updating a priority account." /></figure>
          <div className="lv3-story-copy">
            <p className="lv3-kicker">{content.memory.kicker}</p>
            <h2>{content.memory.title}</h2>
            <p>{content.memory.body}</p>
            <NumberedSteps steps={content.memory.steps} />
          </div>
        </div>
      </section>

      <section className="lv3-how" id="how-it-works">
        <div className="lv3-wrap">
          <div className="lv3-story-heading"><h2>{content.how.title}</h2><p>{content.how.body}</p></div>
          <div className="lv3-how-grid">
            {content.how.steps.map((step, index) => (
              <article key={step.title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{step.title}</h3><p>{step.body}</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="lv3-cta">
        <div className="lv3-wrap">
          <h2>{content.cta.title}</h2>
          <p>{content.cta.body}</p>
          <div className="lv3-cta-actions">
            <a className="lv3-button lv3-button-primary" href="/signup">{content.cta.button} <span aria-hidden="true">→</span></a>
            <a className="lv3-button lv3-button-secondary" href="/download">Download Desktop</a>
          </div>
        </div>
      </section>
    </>
  )
}

export function LandingV3Experience({ content }: LandingV3ExperienceProps) {
  return (
    <div className="lv3-page">
      <Hero content={content.hero} />
      <ContextGap content={content.context} />
      <StorySections content={content} />
    </div>
  )
}

export default LandingV3Experience
